// generateItemListJsonLd.js
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { getHreflang } from '../resource/langPath.js';

/**
 * Generate ItemList JSON-LD for list pages with multi-language support
 * This function is designed for Next.js RRv7 server-side rendering
 *
 * @param {Object} params
 * @param {Object} params.fullUrl - Full URL object with origin and pathname
 * @param {string} params.pageId - Page identifier for @id (e.g., 'feed', 'itemlist')
 * @param {string} params.titleKey - Translation key for title
 * @param {string} params.descriptionKey - Translation key for description (optional)
 * @param {Array<string>} params.itemIds - Array of item IDs
 * @param {Function} params.getItemId - Function to generate item @id from itemId and origin
 * @param {Object} params.i18n - i18n instance (required)
 * @returns {Promise<Object>} JSON-LD object with CollectionPage, ItemList, and BreadcrumbList
 */
export const generateItemListJsonLd = async ({
  fullUrl,
  pageId = 'itemlist',
  titleKey,
  descriptionKey,
  itemIds = [],
  getItemId = (itemId, origin) => `${origin}/item/${itemId}`,
  i18n,
}) => {
  const t = i18n.t;
  const { origin, pathname } = fullUrl;
  const currentLanguage = i18n.language;

  const pageUrl = `${origin}${pathname}`;
  const normalizedInLanguage = getHreflang(currentLanguage);

  const loadLanguagePromise = async () => {
    if (!i18n.hasResourceBundle(currentLanguage, TranslationNamespace.SEO)) {
      await i18n.reloadResources(currentLanguage, TranslationNamespace.SEO);
    }
  };

  await loadLanguagePromise();

  // Helper function to get translated title by language
  const getTitleByLang = lang => {
    return t(titleKey, {
      lng: lang,
      ns: TranslationNamespace.SEO,
    });
  };

  // Helper function to get translated description by language
  const getDescriptionByLang = lang => {
    return t(descriptionKey, {
      lng: lang,
      ns: TranslationNamespace.SEO,
    });
  };

  // Helper function to get home title by language
  const getHomeTitleByLang = lang => {
    return t('seo-home-title', {
      lng: lang,
      ns: TranslationNamespace.SEO,
    });
  };

  const buildNameValueByLang = (lang, translateFn) => translateFn(lang);

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        '@id': `${pageUrl}#webpage`,
        url: pageUrl,
        inLanguage: normalizedInLanguage,
        name: buildNameValueByLang(currentLanguage, getTitleByLang),
        ...(descriptionKey && {
          description: buildNameValueByLang(
            currentLanguage,
            getDescriptionByLang
          ),
        }),
        breadcrumb: {
          '@id': `${pageUrl}#breadcrumb`,
        },
        mainEntity: {
          '@id': `${pageUrl}#${pageId}`,
        },
      },
      {
        '@type': 'ItemList',
        '@id': `${pageUrl}#${pageId}`,
        url: pageUrl,
        itemListElement: itemIds.map((itemId, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@id': getItemId(itemId, origin),
          },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${pageUrl}#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: buildNameValueByLang(currentLanguage, getHomeTitleByLang),
            item: {
              '@id': `${origin}/#website`,
            },
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: buildNameValueByLang(currentLanguage, getTitleByLang),
            item: {
              '@id': `${pageUrl}#webpage`,
            },
          },
        ],
      },
    ],
  };
};

export default generateItemListJsonLd;
